/**
 * Tiny bilingual helper for the consumer Monitor (English + Hebrew, RTL-aware).
 *
 * Deliberately dependency-free: a flat string table + a `t(key, lang)` lookup
 * and a `dir(lang)` helper. The chosen language persists in localStorage so a
 * visitor's choice survives a reload.
 */

export type Lang = 'en' | 'he';

const STORE_KEY = 'gi_monitor_lang';

/** Read the saved language (default English). */
export function getLang(): Lang {
  if (typeof window === 'undefined') return 'en';
  return window.localStorage.getItem(STORE_KEY) === 'he' ? 'he' : 'en';
}

/** Persist the chosen language. */
export function saveLang(lang: Lang): void {
  if (typeof window !== 'undefined') window.localStorage.setItem(STORE_KEY, lang);
}

/** Text direction for a language. */
export function dir(lang: Lang): 'rtl' | 'ltr' {
  return lang === 'he' ? 'rtl' : 'ltr';
}

type Entry = { en: string; he: string };

const STR: Record<string, Entry> = {
  scanning: { en: 'Scanning your phone…', he: '…סורקים את הטלפון שלך' },
  trackers_found: { en: 'trackers found on your phone', he: 'עוקבים נמצאו בטלפון שלך' },
  found: { en: 'found', he: 'נמצאו' },
  blocked: { en: 'blocked', he: 'נחסמו' },
  still_watching: { en: 'still watching', he: 'עדיין עוקבים' },
  block: { en: 'Block', he: 'חסום' },
  blocked_badge: { en: 'Blocked', he: 'נחסם' },
  touched: { en: 'touched', he: 'נגעו ב' },
  times: { en: 'times', he: 'פעמים' },
  saved_this_month: { en: 'saved this month', he: 'נחסכו החודש' },
  savings_sub: {
    en: 'Monitor cancels what you forgot you were paying for.',
    he: 'מוניטור מבטל את מה ששכחת שאתה משלם עליו.',
  },
  start_free: { en: 'Start free →', he: '→ התחילו בחינם' },
  free_line: {
    en: 'Free forever — full scan, unlimited blocking, your savings counter.',
    he: 'חינם לתמיד — סריקה מלאה, חסימה ללא הגבלה, מונה החיסכון שלך.',
  },
  hero_title: { en: "See who's watching you.", he: '.ראה מי עוקב אחריך' },
  hero_sub: {
    en: 'A friend that quietly guards your privacy and your wallet.',
    he: 'חבר ששומר בשקט על הפרטיות והכסף שלך.',
  },
  block_all: { en: 'Block all of them', he: 'חסום את כולם' },
  nice: { en: 'Nice — one less app in your business.', he: 'יופי — אפליקציה אחת פחות בעניינים שלך.' },
  proof: { en: 'Every catch is cryptographically signed — show anyone.', he: 'כל תפיסה חתומה קריפטוגרפית — אפשר להראות לכל אחד.' },
};

/** Look up `key` in `lang`, falling back to the key itself. */
export function t(key: keyof typeof STR | string, lang: Lang): string {
  const e = STR[key as string];
  return e ? e[lang] : (key as string);
}
